import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { Check, Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnalysisDocument } from "@/api/analysis-api";
import { cn } from "@/lib/utils";
import {
  activeSectionHtml,
  applyServerSection,
  applySectionAttributes,
  buildDocumentContent,
  findSectionPosition,
  htmlToPlainText,
  replaceRangeIfUnchanged,
  selectionInfo,
  serializeSections,
  type SelectionInfo,
} from "./document-html";
import { AskAiRevision } from "./ask-ai-revision";
import { EditorToolbar } from "./editor-toolbar";
import { analysisSchemaExtensions, buildAnalysisExtensions } from "./extensions";
import type { AnalysisSectionHandlers } from "./section-node";
import { SlashMenu } from "./slash-menu";
import type { SlashBridge, SlashMenuState } from "./slash-command";

/**
 * The analysis document, open for editing.
 *
 * Two writers touch the same document: the person typing, and the worker
 * filling in sections. Everything below exists to keep them apart —
 * per-section reconciliation, a dirty set that survives a refetch, and a flush
 * on unmount so switching analyses does not drop the last keystrokes.
 */

const AUTOSAVE_DELAY_MS = 1200;

interface AnalysisDocumentEditorProps {
  document: AnalysisDocument;
  onRegenerateSection: (sectionId: string) => void;
  onSaveSection: (sectionId: string, html: string) => Promise<void>;
  /**
   * Ask for a revision of a selected passage. Returns the proposed HTML and
   * writes nothing — the author accepts or discards it.
   */
  onReviseSelection: (params: {
    sectionId: string;
    instruction: string;
    passage: string;
  }) => Promise<string>;
  /** True while any section is generating. */
  busy: boolean;
}

export function AnalysisDocumentEditor({
  document: analysis,
  onRegenerateSection,
  onSaveSection,
  onReviseSelection,
  busy,
}: AnalysisDocumentEditorProps) {
  const [slashState, setSlashState] = useState<SlashMenuState | null>(null);
  /**
   * The selection an Ask-AI request was opened for.
   *
   * Captured when the dialog opens rather than read when it closes: the document
   * stays editable while the author reads the diff, so the live selection may be
   * somewhere else entirely by then.
   */
  const [askingAbout, setAskingAbout] = useState<SelectionInfo | null>(null);
  const [staleSelection, setStaleSelection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Handlers change every render; the editor is built once. A ref is the only
  // thing that can bridge the two without rebuilding the editor on every
  // keystroke.
  const handlersRef = useRef<AnalysisSectionHandlers>({});
  handlersRef.current = { onRegenerate: onRegenerateSection, busy };

  const slashBridgeRef = useRef<SlashBridge>({ setState: () => {} });
  slashBridgeRef.current.setState = setSlashState;

  const schemaExtensions = useMemo(() => analysisSchemaExtensions(), []);

  /** HTML as the server last confirmed it, per section. */
  const savedHtmlRef = useRef<Record<string, string>>({});
  /** `generated_at` of the last server write applied, per section. */
  const appliedRef = useRef<Record<string, string>>({});
  const saveTimerRef = useRef<number | null>(null);

  /**
   * Everything below reaches the editor and the save callback through refs.
   *
   * `useEditor` keeps the options object from the render that created it, so an
   * `onUpdate` that closed over `editor` directly would capture the render where
   * it was still null — and autosave would silently never fire.
   */
  const editorRef = useRef<Editor | null>(null);
  const onSaveSectionRef = useRef(onSaveSection);
  onSaveSectionRef.current = onSaveSection;

  /**
   * The section the cursor is in, serialised on each keystroke.
   *
   * The debounced save re-reads the whole document, so this exists for one case
   * the debounce cannot cover: unmounting. Tiptap destroys the editor in its own
   * effect cleanup, which runs before ours, so by the time we could flush there
   * is nothing left to read. One section's worth of serialising per keystroke is
   * cheap; the whole document's would not be.
   */
  const unsavedRef = useRef<Record<string, string>>({});

  /**
   * True while the component is writing server content into the editor.
   *
   * Those writes are transactions, so they fire `onUpdate` exactly like typing
   * does. Without this the editor would save the text the worker just wrote
   * straight back, and the server would stamp `edited_at` on a section nobody
   * had touched — which then makes a full regenerate skip it as hand-written.
   */
  const applyingRef = useRef(false);

  // Declared before the editor so `onUpdate` can reach it; assigned below, once
  // the real function exists.
  const scheduleSaveRef = useRef<() => void>(() => {});

  const editor = useEditor(
    {
      extensions: buildAnalysisExtensions({
        handlers: handlersRef,
        slashBridge: slashBridgeRef,
      }),
      content: buildDocumentContent(analysis.sections, schemaExtensions),
      editorProps: {
        attributes: {
          class: "focus:outline-none",
          "aria-label": `${analysis.template_name} document`,
        },
      },
      onUpdate: ({ editor: current }) => {
        if (applyingRef.current) return;
        const active = activeSectionHtml(current);
        if (active) unsavedRef.current[active.sectionId] = active.html;
        scheduleSaveRef.current();
      },
    },
    // Rebuilt when the open analysis changes — a different document is a
    // different schema instance's worth of content, and reusing the editor
    // risks mixing the two.
    [analysis._id]
  );

  editorRef.current = editor;

  /**
   * Seed the baselines the autosave diffs against.
   *
   * Read back out of the editor rather than taken from the server's HTML,
   * because the two are not the same string for the same content: Tiptap
   * normalises on parse — `<li>text</li>` comes back as `<li><p>text</p></li>`,
   * a table gains its column widths. Comparing against the server's raw HTML
   * made every section look edited the moment it loaded.
   */
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    savedHtmlRef.current = serializeSections(editor);
    unsavedRef.current = {};
    appliedRef.current = Object.fromEntries(
      analysis.sections.map((section) => [
        section.section_id,
        section.generated_at ?? "",
      ])
    );
    setSavedAt(null);
    // Only on document identity change: re-seeding on every poll would erase the
    // record of what is unsaved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis._id, editor]);

  /**
   * Write back every section whose HTML differs from what the server has.
   *
   * Reads the live editor when there is one, and falls back to what was recorded
   * on the way past — which is what makes the unmount flush work.
   */
  const flushSave = useCallback(async () => {
    const active = editorRef.current;
    const current =
      active && !active.isDestroyed
        ? { ...unsavedRef.current, ...serializeSections(active) }
        : { ...unsavedRef.current };

    const changed = Object.entries(current).filter(
      ([sectionId, html]) => savedHtmlRef.current[sectionId] !== html
    );
    if (!changed.length) return;

    setSaving(true);
    try {
      for (const [sectionId, html] of changed) {
        await onSaveSectionRef.current(sectionId, html);
        savedHtmlRef.current[sectionId] = html;
        delete unsavedRef.current[sectionId];
      }
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  }, []);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      void flushSave();
    }, AUTOSAVE_DELAY_MS);
  }, [flushSave]);

  scheduleSaveRef.current = scheduleSave;

  // Keystrokes inside the debounce window would otherwise be lost when the user
  // switches analyses or leaves the tab.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      void flushSave();
    };
  }, [flushSave]);

  /**
   * Fold a refetched document into the open editor.
   *
   * Only sections the worker has rewritten since we last looked are replaced,
   * and only their range is touched — so a section being edited three sections
   * down keeps its unsaved text while another finishes generating.
   */
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // None of what follows is the user's doing, so none of it should be saved
    // back or counted as an edit.
    applyingRef.current = true;
    try {
      const missing = analysis.sections.some(
        (section) => !findSectionPosition(editor, section.section_id)
      );
      if (missing) {
        // The template gained or lost sections. Rebuilding is safe here: this
        // only happens on a generate, which is an explicit action.
        editor.commands.setContent(
          buildDocumentContent(analysis.sections, schemaExtensions)
        );
      }

      const applied: string[] = [];

      analysis.sections.forEach((section) => {
        const generatedAt = section.generated_at ?? "";
        const alreadyApplied = appliedRef.current[section.section_id];

        if (missing || (generatedAt && generatedAt !== alreadyApplied)) {
          applyServerSection(editor, section, schemaExtensions);
          appliedRef.current[section.section_id] = generatedAt;
          applied.push(section.section_id);
          return;
        }

        applySectionAttributes(editor, section);
      });

      // Re-baseline what we just wrote, from the editor rather than from the
      // server's HTML — see the seeding effect above.
      if (applied.length) {
        const normalised = serializeSections(editor);
        applied.forEach((sectionId) => {
          savedHtmlRef.current[sectionId] = normalised[sectionId] ?? "";
          delete unsavedRef.current[sectionId];
        });
      }
    } finally {
      applyingRef.current = false;
    }
  }, [analysis, editor, schemaExtensions]);

  // Editing is paused while sections are being written — the worker is about to
  // overwrite them, and typing into text that is going to be replaced loses
  // work with no warning.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(!busy);
  }, [busy, editor]);

  if (!editor) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Opening document
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <EditorToolbar editor={editor} disabled={busy} />

      {/* No scroll container of its own: the deal page already scrolls, and
          nesting a second scroller strands the toolbar and breaks the browser's
          find-on-page. `sticky` in the toolbar attaches to the page's scroller. */}
      <div className="relative">
        <div className="mx-auto max-w-3xl px-6 py-6">
          <div className="mb-6 flex items-baseline justify-between gap-3">
            <h1 className="text-xl font-semibold text-foreground">
              {analysis.template_name}
            </h1>
            <SaveIndicator saving={saving} savedAt={savedAt} busy={busy} />
          </div>

          <EditorContent editor={editor} />
        </div>

        <SlashMenu state={slashState} bridge={slashBridgeRef} />

        {/*
          Offered on a selection inside a section, and only when nothing is
          generating — a revision of text the worker is about to replace would be
          discarded either way.
        */}
        <BubbleMenu
          editor={editor}
          shouldShow={({ editor: current }) =>
            !busy && selectionInfo(current) !== null
          }
          className="flex items-center gap-1 rounded-md border border-border bg-popover p-1 shadow-md"
        >
          <button
            type="button"
            onClick={() => {
              const selection = selectionInfo(editor);
              if (!selection) return;
              // The captured range is snapped to whole words, so move the visible
              // highlight to match — otherwise the author reviews a diff of more
              // text than they appear to have selected.
              editor.commands.setTextSelection({
                from: selection.from,
                to: selection.to,
              });
              setStaleSelection(false);
              setAskingAbout(selection);
            }}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-foreground hover:bg-accent"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            {/* Not "Ask AI": the deal header already has a button by that name
                which opens the chat, and these do different things. */}
            Edit with AI
          </button>
        </BubbleMenu>
      </div>

      {askingAbout && (
        <AskAiRevision
          open
          onOpenChange={(next) => {
            if (!next) {
              setAskingAbout(null);
              setStaleSelection(false);
            }
          }}
          selectionText={askingAbout.text}
          sectionTitle={askingAbout.sectionTitle}
          onRequest={async (instruction) => {
            const html = await onReviseSelection({
              sectionId: askingAbout.sectionId,
              instruction,
              passage: askingAbout.html,
            });
            // The diff is computed on text, so the proposal carries both: the
            // HTML is what gets applied, the text is what the author compares.
            return { html, text: htmlToPlainText(html) };
          }}
          onAccept={(html) => {
            const applied = replaceRangeIfUnchanged(
              editor,
              { from: askingAbout.from, to: askingAbout.to },
              askingAbout.text,
              html
            );
            // Refused rather than written to the wrong place — see
            // `replaceRangeIfUnchanged`.
            setStaleSelection(!applied);
            setAskingAbout(null);
          }}
        />
      )}

      {staleSelection && (
        <div className="mx-auto mb-4 max-w-3xl px-6">
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            That text changed while you were reviewing, so the revision wasn't
            applied. Select it again and re-run to be sure of what you're
            replacing.
          </p>
        </div>
      )}
    </div>
  );
}

const SaveIndicator = ({
  saving,
  savedAt,
  busy,
}: {
  saving: boolean;
  savedAt: number | null;
  busy: boolean;
}) => {
  if (busy) {
    return (
      <span className="flex items-center gap-1 text-xs text-primary">
        <Loader2 className="h-3 w-3 animate-spin" />
        Writing — editing paused
      </span>
    );
  }
  if (saving) {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className={cn("flex items-center gap-1 text-xs text-muted-foreground")}>
        <Check className="h-3 w-3" />
        Saved
      </span>
    );
  }
  return null;
};
